import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, ArrowUpRight, Lock, Gift, Copy } from "lucide-react";
import { bandMeta } from "@/lib/api";

export const WELLBEING_TEXT_BEFORE =
  "Applying to university can be stressful. StatementMate gives automated feedback and isn't a support service — if you're finding things hard, please talk to someone you trust, or find a helpline in your country at ";

export const HelplineLink = () => (
  <a
    href="https://findahelpline.com"
    target="_blank"
    rel="noreferrer"
    className="underline underline-offset-2"
    data-testid="findahelpline-link"
  >
    findahelpline.com
  </a>
);

export const ScoreRing = ({ band }) => {
  const meta = bandMeta(band);
  const r = 54;
  const c = 2 * Math.PI * r;
  const offset = c - meta.fraction * c;
  return (
    <div className="relative h-40 w-40" data-testid="overall-score">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#E1DCD1" strokeWidth="10" />
        <motion.circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke={meta.color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
        <span className="font-heading text-xl sm:text-2xl leading-tight" style={{ color: meta.color }}>
          {band}
        </span>
        <span className="text-xs text-ink-muted mt-1">overall</span>
      </div>
    </div>
  );
};

export const FeedbackBody = ({ fb }) => (
  <div className="grid gap-6 sm:grid-cols-2">
    <div>
      <div className="flex items-center gap-2 text-forest text-sm font-semibold uppercase tracking-wide">
        <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
        What's working
      </div>
      <ul className="mt-3 space-y-2">
        {fb.working.map((w, i) => (
          <li key={`w-${i}-${w.slice(0, 24)}`} className="text-ink-soft leading-relaxed flex gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-forest shrink-0" />
            {w}
          </li>
        ))}
      </ul>
    </div>
    <div>
      <div className="flex items-center gap-2 text-clay text-sm font-semibold uppercase tracking-wide">
        <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
        How to improve
      </div>
      <ul className="mt-3 space-y-2">
        {fb.improvements.map((w, i) => (
          <li key={`i-${i}-${w.slice(0, 24)}`} className="text-ink-soft leading-relaxed flex gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-clay shrink-0" />
            {w}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export const LockedBody = () => (
  <div
    className="relative rounded-xl border border-dashed border-clay/30 bg-sand-deep/40 px-6 py-8 text-center"
    data-testid="locked-feedback"
  >
    <div className="grid h-10 w-10 place-items-center rounded-xl bg-clay/10 text-clay mx-auto">
      <Lock className="h-5 w-5" strokeWidth={2} />
    </div>
    <p className="mt-3 text-sm text-ink-muted leading-relaxed max-w-sm mx-auto">
      This feedback is part of your full report. Unlock below to see what's working and
      how to improve.
    </p>
  </div>
);

export const RewardCoupon = ({ code }) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45 }}
    className="rounded-2xl border border-forest/30 bg-forest/[0.06] p-6"
    data-testid="reward-coupon"
  >
    <div className="flex items-start gap-3">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-forest/15 text-forest">
        <Gift className="h-5 w-5" strokeWidth={2} />
      </div>
      <div className="flex-1">
        <h3 className="font-heading text-lg text-ink">Use this feedback and resubmit for 25% off</h3>
        <p className="mt-1 text-sm text-ink-soft leading-relaxed">
          Make the improvements below, then resubmit your statement within the next 24 hours
          and enter this code for 25% off:
        </p>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <code
            data-testid="reward-coupon-code"
            className="rounded-lg bg-white border border-forest/30 px-4 py-2 font-mono text-base font-semibold tracking-widest text-forest"
          >
            {code}
          </code>
          <button
            type="button"
            data-testid="reward-coupon-copy"
            onClick={() => {
              navigator.clipboard?.writeText(code);
              toast.success("Coupon copied.");
            }}
            className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
          >
            <Copy className="h-4 w-4" strokeWidth={2} /> Copy
          </button>
        </div>
      </div>
    </div>
  </motion.section>
);
